import type { ResourcesConfig } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import 'aws-amplify/auth/enable-oauth-listener';
import type { CognitoConfig } from '../cognito-config';
import type { SocialAuthListener } from '../../types';
import {
  HUB_EVENT_SIGN_IN_WITH_REDIRECT,
  HUB_EVENT_SIGN_IN_WITH_REDIRECT_FAILURE,
  HUB_EVENT_CUSTOM_OAUTH_STATE,
} from './provider';

export type MergeAmplifyConfigFn = (partial: ResourcesConfig) => void;

let isAmplifyAuthConfigured = false;

export function configureAmplifyAuth(
  cognitoConfig: CognitoConfig,
  mergeConfig: MergeAmplifyConfigFn,
  listener: SocialAuthListener,
): boolean {
  if (isAmplifyAuthConfigured) {
    return true;
  }

  if (!cognitoConfig.userPoolId || !cognitoConfig.clientId) {
    console.error(
      '[auth] Cognito auth is enabled but COGNITO_USER_POOL_ID / COGNITO_CLIENT_ID are missing. ' +
      'Social sign-in buttons will not work. Set these env vars and rebuild.',
    );
    return false;
  }

  const domain = cognitoConfig.domain.replace(/^https?:\/\//, '');

  mergeConfig({
    Auth: {
      Cognito: {
        userPoolId: cognitoConfig.userPoolId,
        userPoolClientId: cognitoConfig.clientId,
        loginWith: {
          oauth: {
            domain,
            scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [cognitoConfig.redirectSignIn],
            redirectSignOut: [cognitoConfig.redirectSignOut],
            responseType: 'code',
            providers: ['Google', 'Facebook', 'Apple'],
          },
        },
      },
    },
  } as ResourcesConfig);

  // Set the guard BEFORE Hub.listen so concurrent calls during SSR startup
  // cannot register a second listener before the first one completes.
  isAmplifyAuthConfigured = true;

  Hub.listen('auth', ({ payload }) => {
    console.debug('[auth] Hub event:', payload.event);
    if (payload.event === HUB_EVENT_CUSTOM_OAUTH_STATE) {
      listener.setSocialRedirectState(String(payload.data ?? ''));
    }
    if (payload.event === HUB_EVENT_SIGN_IN_WITH_REDIRECT) {
      listener.cognitoSocialSignInComplete();
    }
    if (payload.event === HUB_EVENT_SIGN_IN_WITH_REDIRECT_FAILURE) {
      const data = payload.data as unknown as { error?: { message?: string } } | undefined;
      listener.failSocialAuth(data?.error?.message ?? 'Social sign-in failed');
    }
  });
  return true;
}

/** @internal */
export function _resetAmplifyAuthConfigForTests(): void {
  isAmplifyAuthConfigured = false;
}
